import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { MenuItem, InlineSelect, Tooltip, Typography } from 'seagull';
import styles from "./BillingInitiationControl.module.scss";
import { activeAgreementCannotModifiyTriggerTooltip, makeInitialOneTimeInStages, serviceBillingStagesTermsOptions, serviceBillingTriggerOptions } from "../../../business-logic/billingLogic";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../../models";
import { isFromAmendment } from "../../../models/service/selectors";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useIoContext } from "../../../contexts";
import { isObject } from 'lodash-es';
import { BillingStagesSelect } from "../../ServiceCreation/ServiceBuilder/BillingStagesSelect/BillingStagesSelect";
import { price } from "../../../models/service-cost";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getTriggerLabel(option) {
  const autoLabel = 'automatically';
  const manualLabel = 'manually';
  const optionLabelMap = {
    approval: autoLabel,
    ongoing: autoLabel,
    manualVariable: manualLabel,
    manualFixed: manualLabel,
    oneTimeInStages: 'in stages'
  };
  return optionLabelMap[option];
}
function renderMenuItem(_ref) {
  let {
    value,
    text,
    subtext,
    testid
  } = _ref;
  return /*#__PURE__*/_jsxs(MenuItem, {
    value: value,
    variant: "multiline",
    "data-testid": testid,
    style: {
      width: 330
    },
    children: [/*#__PURE__*/_jsx(Typography, {
      weight: "normal",
      children: text
    }), /*#__PURE__*/_jsx(Typography, {
      children: subtext
    })]
  }, value);
}
function matchOption(option, matcher) {
  if (option === 'manualFixed' || option === 'manualVariable') {
    matcher.manual();
  } else if (option === 'oneTimeInStages') {
    matcher.inStages();
  } else if (option === 'approval' || option === 'ongoing') {
    matcher.auto();
  }
}
function getTriggerValue(service) {
  const manualOption = () => serviceCostModel.isFixedPrice(service.cost) ? 'manualFixed' : 'manualVariable';
  return serviceBillingModel.accept(service.billing, {
    visitOneTimeManual: manualOption,
    visitOnApproval: () => 'approval',
    visitOneTimeInStages: () => 'oneTimeInStages',
    visitOngoing: () => 'ongoing',
    visitRepeatableManual: manualOption
  });
}
export default function BillingInitiationControl(props) {
  var _service$cost$discoun;
  const {
    testid,
    service,
    disabled,
    agreementData,
    isExistingService,
    isFreshlyAdded,
    onChange = () => {}
  } = props;
  const isDisabled = !!disabled;
  const disabledReason = isObject(disabled) ? disabled.reason : undefined;
  const featureFlags = useFeatureFlags();
  const {
    IN_STAGES_OCCURENCE
  } = featureFlags;
  const {
    getNow
  } = useIoContext();
  const now = useMemo(() => getNow(), []);
  const isFromAgreement = agreementData.isActiveAgreement();
  const triggerValue = getTriggerValue(service);
  const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
  const termsOptions = serviceBillingStagesTermsOptions({
    now,
    agreementEffectiveDate: agreementData.effectiveDate,
    isFreshlyAddedToApprovedAgreement: isFromAgreement && !isExistingService
  });
  const triggerOptions = serviceBillingTriggerOptions({
    recurrencePeriod: serviceBillingModel.recurrencePeriod(service.billing),
    pricingType: service.cost.pricingType,
    billingTrigger: service.billing.trigger,
    isFromAgreement,
    isExistingService,
    isFreshlyAdded,
    featureFlags
  });
  function handleTriggerChange(event) {
    const option = event.target.value;
    const setInStages = () => onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
    const setOnApproval = () => onChange(serviceModel.setOnApproval(service));
    const setOneTimeManual = () => onChange(serviceModel.setOneTimeManual(service));
    const doNothing = () => {};
    serviceBillingModel.accept(service.billing, {
      visitOneTimeManual: () => matchOption(option, {
        manual: doNothing,
        auto: setOnApproval,
        inStages: setInStages
      }),
      visitOnApproval: () => matchOption(option, {
        manual: setOneTimeManual,
        auto: doNothing,
        inStages: setInStages
      }),
      visitOneTimeInStages: () => matchOption(option, {
        manual: setOneTimeManual,
        auto: setOnApproval,
        inStages: doNothing
      }),
      visitRepeatableManual: () => matchOption(option, {
        manual: doNothing,
        auto: () => onChange(serviceModel.setOngoing(service)),
        inStages: doNothing
      }),
      visitOngoing: () => matchOption(option, {
        manual: () => onChange(serviceModel.setRepeatableManual(service)),
        auto: doNothing,
        inStages: doNothing
      })
    });
  }
  function handleTermsChange(event) {
    if (!oneTimeInStages) return;
    const terms = event.target.value;
    onChange(serviceModel.setOneTimeInStages(service, oneTimeInStages.setTerms(terms)));
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(Typography, {
      children: ["Billing is initiated", ' ', /*#__PURE__*/_jsxs("div", {
        className: styles.dropdowns,
        children: [triggerOptions.length < 2 || IN_STAGES_OCCURENCE && oneTimeInStages ? /*#__PURE__*/_jsx(Typography, {
          as: "span",
          weight: "normal",
          children: getTriggerLabel(triggerValue)
        }) : /*#__PURE__*/_jsx(Tooltip, {
          placement: "top",
          "data-testid": "".concat(testid, "-trigger-tooltip"),
          title: disabledReason === 'existingServiceInAgreement' ? activeAgreementCannotModifiyTriggerTooltip : '',
          children: /*#__PURE__*/_jsx(InlineSelect, {
            skin: "primary",
            value: triggerValue,
            "data-testid": "".concat(testid, "-trigger"),
            disabled: isDisabled,
            onChange: handleTriggerChange,
            menuMaxHeight: 500,
            SelectProps: {
              renderValue: value => getTriggerLabel(value)
            },
            children: triggerOptions.map(_ref2 => {
              let {
                value,
                text,
                subtext
              } = _ref2;
              return renderMenuItem({
                value,
                text,
                subtext,
                testid: "".concat(testid, "-trigger-option-").concat(value)
              });
            })
          })
        }), oneTimeInStages && /*#__PURE__*/_jsx(InlineSelect, {
          skin: "primary",
          value: oneTimeInStages.terms(),
          "data-testid": "".concat(testid, "-terms"),
          disabled: isDisabled,
          onChange: handleTermsChange,
          SelectProps: {
            renderValue: value => {
              const selected = termsOptions.find(opt => opt.value === value);
              return selected ? selected.text : '';
            }
          },
          children: termsOptions.map(_ref3 => {
            let {
              value,
              text,
              subtext
            } = _ref3;
            return renderMenuItem({
              value,
              text,
              subtext,
              testid: "".concat(testid, "-terms-option-").concat(value)
            });
          })
        })]
      })]
    }), oneTimeInStages && /*#__PURE__*/_jsx(BillingStagesSelect, {
      discount: ((_service$cost$discoun = service.cost.discount) === null || _service$cost$discoun === void 0 ? void 0 : _service$cost$discoun.amount()) || new Decimal(0),
      totalRate: price(service.cost),
      effectiveDate: agreementData.effectiveDate,
      oneTimeInStagesBilling: oneTimeInStages,
      setOneTimeInStagesBilling: oneTimeInStagesBilling => {
        onChange(serviceModel.setOneTimeInStages(service, oneTimeInStagesBilling));
      },
      disabledReason: disabledReason,
      isAmendment: isFromAmendment(service)
    })]
  });
}